import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Observable, of, BehaviorSubject } from 'rxjs'
import { map, catchError } from 'rxjs/operators'
import { Injectable } from '@angular/core'

// NUEVA IMPORTACIÓN: Agregar interfaces de Quote Parameters
import { IQuoteParameters, IQuoteParametersResponse, IUpdateQuoteParameters } from '@modules/private/quote/data/interfaces/iquote-parameters.interface'
import { ApiQuoteParametersService } from '@modules/private/quote/data/services/api/api-quote-parameters.service'
import { Committee } from '@modules/private/lease/data/interfaces/committee.interface'
import { JointObligorFlows, jointObligorsOf } from '../../utils/joint-obligors.util'
import { Billing } from '../../interfaces/billing.interface'
import { ApiLeaseService } from '../api/api-lease.service'
import { BillingService } from '../api/billing.service'
import { CommentsService } from './comments.service'

@Injectable({
  providedIn: 'root',
})
export class LeaseDetailService {
  // ✅ FASE 3: Sistema de notificaciones reactivas para el comité
  private committeeSubject = new BehaviorSubject<Committee | null>(null)
  public committee$ = this.committeeSubject.asObservable()

  entitySession!: FormGroup
  entityNegotiation!: FormGroup
  entityLegal!: FormGroup
  entityPayment!: FormGroup
  entityAcquisition!: FormGroup
  entityRelease!: FormGroup
  entitySettlement!: FormGroup
  formMethod = 'CREATE_JUDGMENT'
  judgments: { label: string; value: string }[] = [
    { label: 'Aprobado', value: 'Aprobado' },
    { label: 'Rechazado', value: 'Rechazado' },
  ]
  statuses: { label: string; value: string }[] = []
  guaranties: { label: string; value: string }[] = [
    { label: 'Hipoteca', value: 'Hipoteca' },
    { label: 'Propia del arrendatario', value: 'Propia del arrendatario' },
    { label: 'Otro', value: 'Otro' },
  ]
  paymentWays: { label: string; value: string }[] = [
    { label: 'Transferencia', value: 'Transferencia' },
    { label: 'Efectivo', value: 'Efectivo' },
    { label: 'Cheque', value: 'Cheque' },
    { label: 'Spei', value: 'Spei' },
    { label: 'Otro', value: 'Otro' },
  ]
  providers: { label: string; value: string }[] = []
  holderBankFlows!: any
  //* F-026 · Uno por hoja «Flujo - OS<n>»; los análisis anteriores traen un solo obligado.
  jointObligorsBankFlows: JointObligorFlows[] = []
  ratiosData!: any
  ratiosId!: string
  calculatorData!: any
  loading = false
  /**
   * Por qué no hay expediente que pintar.
   *
   * `getLeaseDetail` no tenía rama de error: un arrendamiento borrado o un 403 dejaban `data` en
   * null sin decir nada, y la pantalla se quedaba en blanco para siempre. Desde que hay esqueleto
   * de carga eso se vuelve una promesa que no llega, así que el estado de carga necesita su estado
   * terminal.
   */
  loadError = ''

  id!: string
  data!: any

  // Agregar propiedades para billing
  billing: Billing | null = null

  // Nueva propiedad para almacenar termData calculado
  calculatedTermData: any = null

  constructor(
    private fb: FormBuilder,
    private apiLeaseService: ApiLeaseService,
    private billingService: BillingService, // ← INYECTAR BILLING SERVICE
    private apiQuoteParametersService: ApiQuoteParametersService, // ← NUEVA INYECCIÓN
    private commentsService: CommentsService, // ← NUEVO: Servicio de comentarios
  ) {
    this.entitySession = this.fb.group({
      date: ['', [Validators.required]],
      comments: this.fb.array([]),
    })

    this.entityNegotiation = this.fb.group({
      guaranty: ['', [Validators.required]],
      totalValue: ['', [Validators.required]],
      file: ['', [Validators.required]],
    })

    this.entityLegal = this.fb.group({
      file: ['', [Validators.required]],
      score: [null, [Validators.required, Validators.min(1), Validators.max(100)]],
      notes: [''],
    })

    this.entityPayment = this.fb.group({
      paymentWay: ['', [Validators.required]],
      date: ['', [Validators.required]],
      file: ['', [Validators.required]],
    })

    this.entityAcquisition = this.fb.group({
      providerId: ['', [Validators.required]],
      paymentDate: ['', [Validators.required]],
      insurance: [0, [Validators.required]],
      gps: [0, [Validators.required]],
      other: [0, [Validators.required]],
    })

    this.entityRelease = this.fb.group({
      date: ['', [Validators.required]],
      fileRelease: ['', [Validators.required]],
      photos: [[]],
    })

    this.entitySettlement = this.fb.group({
      date: ['', [Validators.required]],
      fileSettlement: ['', [Validators.required]],
      comment: ['', [Validators.required]],
    })

    this.apiLeaseService.getById
  }

  getJudgmentName(id: string) {
    return this.judgments.find((c) => c.value === id)?.label
  }

  getStatusName(id: string) {
    return this.statuses.find((c) => c.value === id)?.label
  }

  getGuarantyName(id: string) {
    return this.guaranties.find((c) => c.value === id)?.label
  }

  getPaymentWayName(id: string) {
    return this.paymentWays.find((c) => c.value === id)?.label
  }

  getProviderName(id: string) {
    return this.providers.find((c) => c.value === id)?.label
  }

  formatDateToInput(dateString: string): string {
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  /**
   * 🧹 RESET CENTRALIZADO - Limpia completamente el estado del servicio
   * Este método debe llamarse antes de cargar un nuevo lease para evitar
   * persistencia de datos entre navegaciones
   */
  resetServiceState(): void {
    // Reset de datos principales
    this.data = null

    // Reset de datos de análisis de riesgo
    this.calculatorData = null
    this.ratiosData = null
    this.ratiosId = ''
    this.holderBankFlows = null
    this.jointObligorsBankFlows = []

    // Reset de billing
    this.billing = null

    // Reset de estados
    this.loading = false
    this.loadError = ''

    // Reset de providers (se vuelven a cargar)
    this.providers = []

    // Reset de formularios
    this.resetAllForms()
  }

  /**
   * 🧹 Reset de todos los formularios del servicio
   */
  private resetAllForms(): void {
    // Reset de todos los FormGroups - manteniendo validadores
    this.entitySession.reset()
    this.entityNegotiation.reset()
    this.entityLegal.reset()
    this.entityPayment.reset()
    this.entityAcquisition.reset()
    this.entityRelease.reset()
    this.entitySettlement.reset()
  }

  /**
   * 🚀 Método público para resetear manualmente el estado
   * Útil para casos especiales donde se necesite limpiar el estado
   */
  public clearServiceState(): void {
    this.resetServiceState()
  }

  /** El análisis de riesgos del arrendamiento (ADR-0021): ratios, calculadora y flujos del titular y de los obligados. */
  private applyRiskAnalysis(lease: any): void {
    this.ratiosData = lease?.ratios ?? null
    this.ratiosId = lease?.ratios?._id ?? null
    this.calculatorData = lease?.calculator ?? null
    this.holderBankFlows = lease?.bankFlows?.holder ? lease.bankFlows.holder : null
    this.jointObligorsBankFlows = jointObligorsOf(lease?.bankFlows)
  }

  /**
   * F-026 · Vuelve a pedir el arrendamiento tras importar o reemplazar el Excel y actualiza SOLO el análisis de riesgos.
   * `getLeaseDetail()` reinicia todo el detalle (datos y formularios de las demás pestañas): la pantalla volvía al
   * esqueleto y se llevaba el cargador con sus avisos.
   */
  refreshRiskAnalysis(): void {
    this.apiLeaseService.getById(this.id).subscribe((r) => {
      if (r.error || !this.data) return
      //* El tipo `ILease` no declara el análisis de riesgos; `getLeaseDetail` también lo lee de `data` sin tipar.
      const lease: any = r.data
      this.data = { ...this.data, ratios: lease?.ratios, calculator: lease?.calculator, bankFlows: lease?.bankFlows }
      this.applyRiskAnalysis(this.data)
    })
  }

  /**
   * F-009 · Tras copiar documentos del cliente, vuelve a pedir el arrendamiento y actualiza SOLO su expediente (la copia
   * ya no devuelve el arrendamiento). Mismo motivo que `refreshRiskAnalysis`: `getLeaseDetail()` regresaba la pantalla al
   * esqueleto. La referencia nueva de `data` es la que hace que el expediente hijo se vuelva a pintar (`ngOnChanges`).
   *
   * Emite `false` si la recarga falló: la copia ya se hizo, pero lo que se ve es el expediente de antes, y quien llama
   * tiene que decirlo en vez de dejar la pantalla desactualizada en silencio.
   */
  refreshDocuments(): Observable<boolean> {
    return this.apiLeaseService.getById(this.id).pipe(
      map((r) => {
        if (r.error) return false
        if (!this.data) return true
        //* `ILease` no declara `documents` ni `documentType`; `lease-documents` también los lee de `data` sin tipar.
        const lease: any = r.data
        this.data = { ...this.data, documents: lease?.documents ?? null, documentType: lease?.documentType ?? null }
        return true
      }),
    )
  }

  getLeaseDetail() {
    // 🔄 NUEVO: Limpiar estado antes de cargar nuevo lease
    this.resetServiceState()

    this.apiLeaseService.getById(this.id).subscribe((r) => {
      if (!r.error) {
        this.getProviders()
        this.data = r.data
        this.applyRiskAnalysis(this.data)

        this.entityNegotiation.patchValue({
          guaranty: this.data.negotiation.guarantee || '',
          totalValue: this.data.negotiation.totalValue || '',
          file: this.data.negotiation.document || '',
        })

        // Optional chaining: los leases viejos no traen la sección `legal`.
        this.entityLegal.patchValue({
          file: this.data.legal?.file || '',
          score: this.data.legal?.score ?? null,
          notes: this.data.legal?.notes || '',
        })

        this.entityPayment.patchValue({
          paymentWay: this.data.initialPayment.paymentType || '',
          date: this.formatDateToInput(this.data.initialPayment.date) || '',
          file: this.data.initialPayment.voucher || '',
        })

        this.entityAcquisition.patchValue({
          providerId: this.data.assetAcquisition.provider || '',
          paymentDate: this.formatDateToInput(this.data.assetAcquisition.paymentDate) || '',
        })

        this.entityRelease.patchValue({
          date: this.formatDateToInput(this.data.assetDelivery.date) || '',
          fileRelease: this.data.assetDelivery.proof || '',
          photos: this.data.assetDelivery.photos || [],
        })

        this.entitySettlement.patchValue({
          date: this.formatDateToInput(this.data.settlement.date) || '',
          fileSettlement: this.data.settlement.settlementFormat || '',
          comment: this.data.settlement.comments || '',
        })

        // Cargar información de billing
        this.loadBillingInfo()

        // NUEVO: Auto-calcular cotización si es necesario
        this.autoCalculateQuotationIfNeeded()

        // NUEVO: Inicializar comentarios del lease cargado
        this.initializeComments()
      } else {
        this.loadError = r.msg || 'No se pudo cargar el arrendamiento.'
      }
    })
  }

  saveNegotiation() {
    const guarantee = {
      negotiation: {
        guarantee: this.entityNegotiation.value.guaranty,
        totalValue: this.entityNegotiation.value.totalValue,
        document: this.entityNegotiation.value.file,
      },
    }

    this.apiLeaseService
      .putCollection(guarantee, undefined, 'leases/' + this.id)
      .pipe()
      .subscribe((r) => {
        if (!r.error) this.getLeaseDetail()
      })
  }

  /**
   * Edita el primer número del folio. `oldId` = ese número (regla del owner: el oldId
   * es EXCLUSIVAMENTE el número 1–999, no el folio anterior). Devuelve el Observable
   * para que el caller gestione loading y feedback (toast).
   */
  updateFolio(folio: string, oldId: string) {
    return this.apiLeaseService.putCollection({ folio, oldId }, undefined, 'leases/' + this.id)
  }

  getProviders() {
    return this.apiLeaseService.getProviders().subscribe((r) => {
      if (!r.error) this.providers = r.data.data.map((p: any) => ({ label: p.fullName, value: p._id }))
    })
  }

  saveEntityAcquisition() {
    const assetAcquisition = {
      assetAcquisition: {
        provider: this.entityAcquisition.value.providerId,
        paymentDate: this.entityAcquisition.value.paymentDate,
      },
    }

    this.apiLeaseService
      .putCollection(assetAcquisition, undefined, 'leases/' + this.id)
      .pipe()
      .subscribe((r) => {
        if (!r.error) this.getLeaseDetail()
      })
  }

  saveRelease() {
    const release = {
      assetDelivery: {
        date: this.entityRelease.value.date,
        proof: this.entityRelease.value.fileRelease,
        photos: this.entityRelease.value.photos || [],
      },
    }

    this.apiLeaseService
      .putCollection(release, undefined, 'leases/' + this.id)
      .pipe()
      .subscribe((r) => {
        if (!r.error) this.getLeaseDetail()
      })
  }

  saveSettlement() {
    const settlement = {
      settlement: {
        date: this.entitySettlement.value.date,
        settlementFormat: this.entitySettlement.value.fileSettlement,
        comments: this.entitySettlement.value.comment,
      },
    }

    this.apiLeaseService
      .putCollection(settlement, undefined, 'leases/' + this.id)
      .pipe()
      .subscribe((r) => {
        if (!r.error) this.getLeaseDetail()
      })
  }

  /**
   * @deprecated Usar el nuevo sistema de comentarios con CommentsService
   * Actualizar los comentarios generales del lease (método legacy)
   * @param comments Nuevos comentarios para el lease
   * @returns Observable con la respuesta del servidor
   */
  updateComments(comments: string) {
    const commentUpdate = {
      comments: comments,
    }

    return this.apiLeaseService.putCollection(commentUpdate, undefined, 'leases/' + this.id).pipe(
      map((response: any) => {
        if (!response.error) {
          // Actualizar los datos locales sin recargar todo
          if (this.data) this.data.comments = comments
          // Opcional: recargar datos completos para asegurar sincronización
          this.getLeaseDetail()
        }
        return response
      }),
      catchError((error: any) => {
        console.error('Error al actualizar comentarios:', error)
        return of({ error: true, msg: 'Error de conexión' })
      }),
    )
  }

  /**
   * Obtener el servicio de comentarios para acceso externo
   */
  getCommentsService(): CommentsService {
    return this.commentsService
  }

  /**
   * Inicializar comentarios para el lease actual
   */
  initializeComments(): void {
    if (this.data && this.data.comments) {
      this.commentsService.setComments(this.data.comments)
    } else {
      this.commentsService.setComments([])
    }
  }

  /**
   * Limpiar estado de comentarios al cambiar de lease
   */
  clearCommentsState(): void {
    this.commentsService.clearState()
  }

  approveLeaseTerm(leaseId: string, quotationId: string, termId: string) {
    return this.apiLeaseService
      .approveLeaseTerm(leaseId, quotationId, termId)
      .pipe()
      .subscribe((r) => {
        if (!r.error) this.getLeaseDetail()
      })
  }

  getCommitteeData(): Committee | null {
    if (!this.data?.committee) return null

    return {
      _id: this.data.committee._id || '',
      overallStatus: this.data.committee.overallStatus || 'Pendiente',
      meetings: this.data.committee.meetings || [],
      generalComments: this.data.committee.generalComments || undefined,
      finalDecisionDate: this.data.committee.finalDecisionDate ? new Date(this.data.committee.finalDecisionDate) : undefined,
    }
  }

  /**
   * ✅ FASE 3: Actualizar el estado reactivo del comité
   */
  updateCommitteeState(committee: Committee | null) {
    this.committeeSubject.next(committee)
  }

  /**
   * 🔄 Actualizar solo la información del comité sin recargar todo el lease
   * Método optimizado para mejorar UX después de guardar reuniones
   */
  refreshCommitteeData(): Observable<Committee | null> {
    return this.apiLeaseService.getById(this.id).pipe(
      map((response: any) => {
        if (!response.error && response.data?.committee) {
          // Actualizar solo los datos del comité en el estado local
          this.data.committee = response.data.committee
          const updatedCommittee = this.getCommitteeData()

          // ✅ FASE 3: Notificar cambios reactivamente
          this.updateCommitteeState(updatedCommittee)

          return updatedCommittee
        }
        return null
      }),
      catchError((error: any) => {
        console.error('Error al actualizar datos del comité:', error)
        return of(null)
      }),
    )
  }

  runCiecVerification() {
    return this.apiLeaseService.runCiecVerification(this.id)
  }

  /** Importa al arrendamiento el análisis que ya existe en Ekatena para su cliente. */
  /**
   * B-004 · Vuelve a pedir el arrendamiento y actualiza SOLO su análisis fiscal: cuando otra persona lo copió primero,
   * la pantalla tiene que dejar de mostrar el vacío de antes. No reinicia el detalle (ver `refreshRiskAnalysis`).
   */
  refreshVerification(): void {
    this.apiLeaseService.getById(this.id).subscribe((r) => {
      const lease: any = r?.data
      if (r?.error || !this.data || !lease?.verification) return
      this.data.verification = lease.verification
    })
  }

  /** B-004 · Copia (o, con `dryRun`, previsualiza) el análisis vigente del cliente en este arrendamiento. */
  copyClientVerification(dryRun = false) {
    return this.apiLeaseService.copyClientVerification(this.id, dryRun)
  }

  importEkatena() {
    return this.apiLeaseService.importEkatena(this.id)
  }

  /**
   * Recorrer la fecha de inicio de la tabla de amortización en cascada. Delega al API
   * service y, si tiene éxito, recarga el detalle completo (la tabla de Exigibilidad y
   * el Resumen de Cobranza se re-renderizan solos al actualizar `data`). Devuelve el
   * Observable para que el componente maneje toasts/cierre de modal.
   */
  editAmortizationStartDate(startDate: string, reason: string, confirmPaidPeriods: boolean): Observable<{ error: boolean; msg: string; data: any }> {
    return this.apiLeaseService.editAmortizationStartDate(this.id, startDate, reason, confirmPaidPeriods).pipe(
      map((r) => {
        if (!r.error) this.getLeaseDetail()
        return r
      }),
    )
  }

  /** Previsualiza una edición de montos: NO escribe nada, devuelve cómo quedarían los saldos. */
  previewLeaseAmounts(payload: any): Observable<{ error: boolean; msg: string; data: any }> {
    return this.apiLeaseService.editLeaseAmounts(this.id, payload, true)
  }

  /** Aplica una edición de montos ya previsualizada y refresca el detalle. */
  applyLeaseAmounts(payload: any): Observable<{ error: boolean; msg: string; data: any }> {
    return this.apiLeaseService.editLeaseAmounts(this.id, payload, false).pipe(
      map((r) => {
        if (!r.error) this.getLeaseDetail()
        return r
      }),
    )
  }

  /**
   * ADR-0021 — Import del Excel estandarizado de Análisis de Riesgo.
   * Sobrescribe bankFlows/ratios/calculator del lease en una sola operación.
   */
  importRiskExcel(file: File) {
    return this.apiLeaseService.importRiskExcel(this.id, file)
  }

  /**
   * ADR-0021 — Reemplazar el Excel del análisis de riesgo (PUT). Solo roles con
   * permiso `risk.re-upload` (Admin y Finanzas) pueden invocarlo; el resto
   * recibe 403. Borra los datos anteriores tras guardar los nuevos.
   */
  replaceRiskExcel(file: File) {
    return this.apiLeaseService.replaceRiskExcel(this.id, file)
  }

  /**
   * ADR-0021 — Descargar el Excel original via proxy backend (blob privado).
   */
  downloadRiskExcel() {
    return this.apiLeaseService.downloadRiskExcel(this.id)
  }

  /**
   * Descargar documento por URL - Método delegado al ApiLeaseService
   */
  downloadDocumentByUrl(fileUrl: string): Observable<Blob> {
    return this.apiLeaseService.downloadDocumentByUrl(fileUrl)
  }

  /**
   * Cargar información de billing desde el lease actual
   */
  loadBillingInfo() {
    if (this.data?.billing) {
      this.billing = this.data.billing
    } else {
      this.billing = null
    }
  }

  /**
   * Actualizar información de billing en el estado local
   */
  updateBillingInfo(billing: Billing) {
    this.billing = billing
    // Actualizar data local
    if (this.data) this.data.billing = billing
  }

  /**
   * Verificar si el lease tiene billing configurado
   */
  hasBilling(): boolean {
    return this.billing !== null
  }

  /**
   * Obtener el billing actual
   */
  getBilling(): Billing | null {
    return this.billing
  }

  // ========== MÉTODOS PARA QUOTE PARAMETERS ==========

  /**
   * Método para actualizar quote parameters desde el comité
   */
  updateQuoteParameters(parameters: IQuoteParameters): Observable<IQuoteParametersResponse> {
    if (!parameters._id) throw new Error('No se encontró el ID de los parámetros de cotización')

    // Crear el objeto con el tipo correcto para el método update
    const updateData: IUpdateQuoteParameters = {
      _id: parameters._id,
      leaseType: parameters.leaseType,
      advance: parameters.advance,
      interestRate: parameters.interestRate,
      openingCommission: parameters.openingCommission,
      advancedRents: parameters.advancedRents,
      ratificationExpenses: parameters.ratificationExpenses,
      terms: parameters.terms,
      // Solo incluir las propiedades que pueden ser actualizadas
      ...(parameters.client && { client: parameters.client }),
      ...(parameters.assets && { assets: parameters.assets }),
      ...(parameters.assetsValue && { assetsValue: parameters.assetsValue }),
      ...(parameters.accessories && { accessories: parameters.accessories }),
    }

    return this.apiQuoteParametersService.update(parameters._id, updateData)
  }

  /**
   * Obtener quote parameters desde los datos del lease
   * Los parámetros están directamente disponibles en this.data.quotation
   */
  getQuoteParameters(): IQuoteParameters | null {
    // CORRECCIÓN: Solo retornar si quotationParameters existe y no es null
    if (this.data?.quotationParameters && this.data.quotationParameters !== null) {
      return this.data.quotationParameters as IQuoteParameters
    }
    return null
  }

  /**
   * Verificar si hay una cotización aprobada (para solo lectura)
   */
  hasApprovedQuotation(): boolean {
    // Verificar que quotationParameters sea null/undefined pero quotation exista
    return (this.data?.quotationParameters === null || !this.data?.quotationParameters) && !!this.data?.quotation
  }

  /**
   * Obtener datos de la cotización aprobada
   */
  getApprovedQuotationData() {
    if (!this.hasApprovedQuotation()) return null

    const quotation = this.data.quotation

    // Verificar si tiene termData (nueva estructura)
    if (quotation?.termData) {
      return {
        type: 'termData',
        data: quotation.termData,
        hasAmortization: !!quotation.amortizationPeriods,
        quotation: quotation,
      }
    }
    // Verificar si tiene approvedTerm (estructura legacy)
    else if (quotation?.approvedTerm) {
      return {
        type: 'approvedTerm',
        data: quotation.approvedTerm,
        hasAmortization: !!quotation.approvedTerm.amortizationTable,
        quotation: quotation,
      }
    }
    // Si solo tiene quotation básica sin termData ni approvedTerm
    else if (quotation) {
      return {
        type: 'basic',
        data: quotation,
        hasAmortization: false,
        quotation: quotation,
      }
    }

    return null
  }

  /**
   * Verificar si hay termData disponible de forma segura
   */
  hasTermData(): boolean {
    return !!(this.data?.quotation?.termData || this.calculatedTermData)
  }

  /**
   * Obtener termData de forma segura (desde quotation o calculado)
   */
  getTermDataSafely(): any | null {
    if (this.data?.quotation?.termData) return this.data.quotation.termData
    if (this.calculatedTermData) return this.calculatedTermData
    return null
  }

  /**
   * Verificar si se necesita calcular la cotización automáticamente
   */
  private shouldAutoCalculateQuotation(): boolean {
    return !!(this.data?.quotationParameters && this.data.quotationParameters._id && !this.data?.quotation?.termData && !this.calculatedTermData)
  }

  /**
   * Calcular cotización automáticamente si es necesario
   */
  private autoCalculateQuotationIfNeeded(): void {
    // Escenario 1: Hay quotationParameters pero no termData - calcular desde parameters
    if (this.data?.quotationParameters && this.data.quotationParameters._id && !this.data?.quotation?.termData && !this.calculatedTermData) {
      this.calculateFromParameters(this.data.quotationParameters._id)
      return
    }

    // Escenario 2: Hay quotation con termData - asignar directamente
    if (this.data?.quotation?.termData && !this.calculatedTermData) {
      this.calculatedTermData = this.data.quotation.termData
      return
    }

    // Escenario 3: Hay quotation sin termData - extraer de selectedTerms o approvedTerm
    if (this.data?.quotation && !this.data?.quotation?.termData && !this.data?.quotationParameters && !this.calculatedTermData) {
      this.extractTermDataFromQuotation()
      return
    }
  }

  /**
   * Calcular cotización desde quotationParameters
   */
  private calculateFromParameters(parametersId: string): void {
    this.apiQuoteParametersService.calculateQuote(parametersId).subscribe({
      next: (response) => {
        if (!response.error && response.data) {
          // Actualizar los datos locales
          if (this.data) {
            this.data.quotation = response.data

            // CORRECCIÓN: Los datos están directamente en response.data como IQuote
            // Verificar si tiene termData directamente
            if (response.data.termData) {
              this.calculatedTermData = response.data.termData
            }
            // O si tiene selectedTerms (estructura alternativa - acceso seguro)
            else if ((response.data as any).selectedTerms && (response.data as any).selectedTerms.length > 0) {
              this.calculatedTermData = (response.data as any).selectedTerms[0]
            }
          }
        }
      },
      error: (error) => {
        console.error('Error al calcular cotización desde parameters:', error)
      },
    })
  }

  /**
   * Extraer termData de quotation existente
   */
  private extractTermDataFromQuotation(): void {
    if (this.data?.quotation) {
      const quotation = this.data.quotation

      // Si la quotation ya tiene selectedTerms
      if (quotation.selectedTerms && quotation.selectedTerms.length > 0) {
        this.calculatedTermData = quotation.selectedTerms[0]
        return
      }

      // Si la quotation tiene approvedTerm (estructura legacy)
      if (quotation.approvedTerm) {
        this.calculatedTermData = quotation.approvedTerm
        return
      }
    }
  }

  /**
   * Método de debugging temporal para verificar datos de termData
   */
  public debugTermData() {
    console.log('=== DEBUG TERM DATA ===')
    console.log('data.quotation:', this.data?.quotation)
    console.log('data.quotationParameters:', this.data?.quotationParameters)
    console.log('calculatedTermData:', this.calculatedTermData)
    console.log('getTermDataSafely():', this.getTermDataSafely())

    const termData = this.getTermDataSafely()
    if (termData) console.log('termData.downPayment:', termData.downPayment)
    console.log('=== END DEBUG ===')
  }
}
